/**
 * Prefetch tiles mappa per uso offline (delega al Service Worker + Cache API `map-tiles`).
 */
import { postPrefetchTilesMessage } from './sw-messages';
import { getOfflineMapAreas } from '../config/market';

const DEFAULT_MIN_ZOOM = 8;
const DEFAULT_MAX_ZOOM = 14;

function envNumber(name, fallback) {
  const v = process.env[name];
  if (v === undefined || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}


/**
 * @param {[number, number, number, number]} bbox minLon, minLat, maxLon, maxLat
 * @param {number} minZoom
 * @param {number} maxZoom
 * @param {(downloaded: number, total: number) => void} [onProgress]
 * @returns {Promise<{ downloaded: number, total: number, failed: number }>}
 */
export async function prefetchMapTiles(bbox, minZoom, maxZoom, onProgress) {
  const mz = Number.isFinite(minZoom) ? minZoom : DEFAULT_MIN_ZOOM;
  const Mz = Number.isFinite(maxZoom) ? maxZoom : DEFAULT_MAX_ZOOM;
  const est = estimateTilesSize(bbox, mz, Mz);




  return postPrefetchTilesMessage(bbox, [mz, Mz], onProgress);
}

/**
 * Prefetch delle aree offline del mercato (una dopo l'altra), zoom da REACT_APP_OFFLINE_*_ZOOM.
 * Il progresso è cumulativo su tutte le aree.
 * @param {(downloaded: number, total: number) => void} [onProgress]
 * @returns {Promise<{ downloaded: number, total: number, failed: number }>}
 */
export async function prefetchMapTilesFromEnv(onProgress) {
  const areas = getOfflineMapAreas();
  const minZ = envNumber('REACT_APP_OFFLINE_MIN_ZOOM', DEFAULT_MIN_ZOOM);
  const maxZ = envNumber('REACT_APP_OFFLINE_MAX_ZOOM', DEFAULT_MAX_ZOOM);
  const estimates = areas.map((area) => estimateTilesSize(area.bbox, minZ, maxZ).tileCount);

  const sum = { downloaded: 0, total: 0, failed: 0 };
  for (let i = 0; i < areas.length; i++) {
    const remaining = estimates.slice(i + 1).reduce((a, b) => a + b, 0);
    const result = await prefetchMapTiles(areas[i].bbox, minZ, maxZ, (processed, total) => {
      onProgress?.(sum.total + processed, sum.total + total + remaining);
    });
    sum.downloaded += result.downloaded;
    sum.total += result.total;
    sum.failed += result.failed;
  }
  return sum;
}

/**
 * Stima approssimativa del numero di tile e della dimensione.
 * @param {[number, number, number, number]} bbox
 * @param {number} minZoom
 * @param {number} maxZoom
 */
export function estimateTilesSize(bbox, minZoom, maxZoom) {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  let count = 0;
  for (let z = minZoom; z <= maxZoom; z++) {
    const nw = latLonToTile(maxLat, minLon, z);
    const se = latLonToTile(minLat, maxLon, z);
    const minX = Math.min(nw.x, se.x);
    const maxX = Math.max(nw.x, se.x);
    const minY = Math.min(nw.y, se.y);
    const maxY = Math.max(nw.y, se.y);
    count += (maxX - minX + 1) * (maxY - minY + 1);
  }
  const estimatedBytes = count * 20 * 1024;
  return {
    tileCount: count,
    estimatedMB: (estimatedBytes / (1024 * 1024)).toFixed(1)
  };
}

/**
 * @param {number} lat
 * @param {number} lon
 * @param {number} zoom
 */
function latLonToTile(lat, lon, zoom) {
  const n = Math.pow(2, zoom);
  const x = Math.floor((lon + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor(
    (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n
  );
  return { x, y };
}